import { FormItem } from '@/components/dashboard/settings/personal-details/FormItem';
import clsx from 'clsx';
import React from 'react';
import {
	FieldErrors,
	FieldValues,
	UseFormRegister,
	UseFormSetValue,
	UseFormWatch,
} from 'react-hook-form';
import { InputText } from '../input-text';
import { InputZipCode } from '../input-zip-code';
import {
	AddressValue,
	InputPlacesAutocomplete,
} from './input-places-autocomplete';

export const parseAddressResponse = (
	value?: string
): {
	address1: string;
	address2: string;
	city: string;
	state: string;
	zip: string;
} | null => {
	if (!value?.trim()) return null;
	const parts = value
		.split(',')
		.map((part) => part.trim())
		.filter(Boolean);
	if (parts.length === 4) {
		return {
			address1: parts[0],
			address2: '',
			city: parts[1],
			state: parts[2],
			zip: parts[3],
		};
	}
	if (parts.length === 5) {
		return {
			address1: parts[0],
			address2: parts[1],
			city: parts[2],
			state: parts[3],
			zip: parts[4],
		};
	}
	return null;
};

export const formatAddressResponse = (
	address1?: string,
	address2?: string,
	city?: string,
	state?: string,
	zip?: string
): string => {
	return [address1, address2, city, state, zip]
		.map((part) => part?.trim())
		.filter(Boolean)
		.join(', ');
};

export interface AddressBlockProps {
	register: UseFormRegister<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	watch: UseFormWatch<FieldValues>;
	errors?: FieldErrors<FieldValues>;
	rules?: any;
	defaultAddress1?: string;
	onGoogleSelect?: (address: AddressValue) => void;
	className?: string;
	classNameInput?: string;
	address1Name?: string;
	address2Name?: string;
	zipName?: string;
	stateDisplayName?: string;
	cityDisplayName?: string;
	/** When set, write `address1[, address2], city, state, zip` into this field. */
	responseName?: string;
	/** Hide City/State/Zip. Sign ADDRESS shows Address 1 + Address 2 only. */
	showLocationFields?: boolean;
	disabled?: boolean;
	initialAddress2?: string;
	initialCityName?: string;
	initialStateName?: string;
	initialZip?: string;
}

export const AddressBlock: React.FC<AddressBlockProps> = ({
	register,
	setValue,
	watch,
	errors,
	rules,
	defaultAddress1,
	onGoogleSelect,
	className,
	classNameInput,
	address1Name = 'address',
	address2Name = 'address2',
	zipName = 'zipCode',
	stateDisplayName = 'stateName',
	cityDisplayName = 'cityName',
	responseName,
	showLocationFields = true,
	disabled = false,
	initialAddress2,
	initialCityName,
	initialStateName,
	initialZip,
}) => {
	const fieldWidth = clsx('w-full md:w-[500px]', classNameInput);
	const useInputTitles = !showLocationFields;
	const address1ItemClassName = clsx(
		className,
		showLocationFields && '!border-b-0'
	);
	const address1 = watch(address1Name);
	const address2 = watch(address2Name);
	const city = watch(cityDisplayName);
	const state = watch(stateDisplayName);
	const zip = watch(zipName);

	React.useEffect(() => {
		if (defaultAddress1) {
			setValue(address1Name, defaultAddress1, { shouldValidate: true });
		}
		if (initialAddress2) {
			setValue(address2Name, initialAddress2, { shouldValidate: true });
		}
		if (initialStateName) {
			setValue(stateDisplayName, initialStateName, { shouldValidate: true });
		}
		if (initialCityName) {
			setValue(cityDisplayName, initialCityName, { shouldValidate: true });
		}
		if (initialZip) {
			setValue(zipName, initialZip, { shouldValidate: true });
		}
	}, [
		address1Name,
		address2Name,
		cityDisplayName,
		defaultAddress1,
		initialAddress2,
		initialCityName,
		initialStateName,
		initialZip,
		setValue,
		stateDisplayName,
		zipName,
	]);

	React.useEffect(() => {
		if (!responseName) return;
		const formatted = formatAddressResponse(
			address1,
			address2,
			city,
			state,
			zip
		);
		const existing = watch(responseName);
		if (!formatted && existing) return;
		setValue(responseName, formatted, { shouldValidate: true });
	}, [address1, address2, city, state, zip, responseName, setValue, watch]);

	return (
		<>
			<FormItem
				label={useInputTitles ? '' : 'Address 1'}
				className={address1ItemClassName}
			>
				<InputPlacesAutocomplete
					name={address1Name}
					title={useInputTitles ? 'Address 1' : undefined}
					placeholder="Ex: 1234 Sunshine Blvd"
					className={fieldWidth}
					register={register}
					setValueInput={setValue}
					defaultValue={defaultAddress1 || ''}
					valueMode="street"
					disabled={disabled}
					onSelect={(address) => {
						setValue(address1Name, address.street, { shouldValidate: true });
						setValue(address2Name, address.address2 ?? '', {
							shouldValidate: true,
						});
						setValue(stateDisplayName, address.state, {
							shouldValidate: true,
						});
						setValue(cityDisplayName, address.city, { shouldValidate: true });
						if (address.zip) {
							setValue(zipName, address.zip, { shouldValidate: true });
						}
						onGoogleSelect?.(address);
					}}
					rules={rules?.address ?? { required: { value: false } }}
					error={errors?.[address1Name]}
				/>
			</FormItem>
			<FormItem label={useInputTitles ? '' : 'Address 2'} className={className}>
				<InputText
					title={useInputTitles ? 'Address 2' : undefined}
					name={address2Name}
					register={register}
					placeholder="Ex: apt #64"
					className={fieldWidth}
					classNameInput="!mt-0"
					maxLength={100}
					disabled={disabled}
					rules={
						rules?.address2 ?? {
							required: { value: false },
							maxLength: {
								value: 100,
								message: 'Address 2 has to have less than 100 characters',
							},
						}
					}
					error={errors?.[address2Name]}
				/>
			</FormItem>
			{showLocationFields ? (
				<>
					<FormItem label="State" className={className}>
						<InputText
							name={stateDisplayName}
							register={register}
							disabled
							placeholder="State"
							className={fieldWidth}
							classNameInput="!mt-0 disabled:!bg-[#F4F4F4A3] disabled:!text-black group-hover:disabled:!bg-[#F4F4F4A3]"
							rules={rules?.state}
							error={errors?.[stateDisplayName]}
						/>
					</FormItem>
					<FormItem label="City" className={className}>
						<InputText
							name={cityDisplayName}
							register={register}
							disabled
							placeholder="City"
							className={fieldWidth}
							classNameInput="!mt-0 disabled:!bg-[#F4F4F4A3] disabled:!text-black group-hover:disabled:!bg-[#F4F4F4A3]"
							rules={rules?.city}
							error={errors?.[cityDisplayName]}
						/>
					</FormItem>
				</>
			) : null}
			{responseName ? (
				<input
					type="hidden"
					{...register(responseName, {
						validate: (value: string) =>
							!!value?.trim() || 'This field is required',
					})}
				/>
			) : null}
			{showLocationFields ? (
				<FormItem label="Zip code" className={className}>
					<InputZipCode
						register={register}
						name={zipName}
						customPlaceholder="Ex: 33101"
						rules={rules?.zipCode}
						error={errors?.[zipName]}
						className={fieldWidth}
						classNameInput="!mt-0 disabled:!bg-[#F4F4F4A3] disabled:!text-black group-hover:disabled:!bg-[#F4F4F4A3]"
						setValueInput={setValue}
						disabled={disabled || Boolean(zip)}
					/>
				</FormItem>
			) : null}
		</>
	);
};
